import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { isValidPassengersNumber } from 'utils/validators';
import { ErrorField, StyledInput } from 'pages/ManageClubEurostar/ManageClubEurostar.styles';
import Label from 'components/ui/Label';
import { AssistanceContainer, AssistanceField, AssistanceLabel, StyledAssistancePax } from './AddPassengersCta.styles';

const MAX_NUMBER_OF_WHEELCHAIR_USERS = 4;

const WheelchairPassengers = ({
  register,
  getValues,
  setValue,
  trigger,
  errors,
  numberOfWheelchairUsers,
  numberOfWheelchairCompanions,
  numberOfNonWheelchairCompanions,
  actionComponent
}) => {
  useEffect(() => {
    setValue(`nonWheelchairCompanions`, 0);
  }, [setValue]);
  const { wheelchairAdults, wheelchairChild, wheelchairCompanions } = getValues();
  const totalWheelchairUsers = Number(wheelchairAdults || 0) + Number(wheelchairChild || 0) + numberOfWheelchairUsers;
  const totalWheelchairCompanions = Number(wheelchairCompanions || 0) + numberOfWheelchairCompanions;
  const totalNonWheelchairCompanions = numberOfNonWheelchairCompanions;

  const handleValidateWheelchairUsers = (value) =>
    !value || (isValidPassengersNumber(value) && totalWheelchairUsers <= MAX_NUMBER_OF_WHEELCHAIR_USERS);
  const handleValidateWheelchairCompanions = (value) =>
    !value || (isValidPassengersNumber(value) && totalWheelchairCompanions <= totalWheelchairUsers);

  useEffect(() => {
    if (trigger) {
      trigger('wheelchairAdults');
      trigger('wheelchairChild');
      trigger('wheelchairCompanions');
    }
  }, [totalWheelchairUsers, totalWheelchairCompanions, trigger]);

  return (
    <StyledAssistancePax>
      <AssistanceContainer>
        <AssistanceLabel>
          <FormattedMessage id="addPassengers.newSpecialAssistancePassengers" />
        </AssistanceLabel>
        <AssistanceField>
          <Label htmlFor="wheelchairAdults">
            <FormattedMessage id="addPassengers.wheelchairAdults" />
          </Label>
          <StyledInput
            {...register('wheelchairAdults', {
              validate: handleValidateWheelchairUsers
            })}
            type="number"
            inputMode="numeric"
            id="wheelchairAdults"
            data-testid="wheelchair-adults"
            data-cy="wheelchair-adults"
            validationState={errors.wheelchairAdults ? 'error' : ''}
            min={0}
            max={MAX_NUMBER_OF_WHEELCHAIR_USERS}
          />
        </AssistanceField>
        <AssistanceField>
          <Label htmlFor="wheelchairChild">
            <FormattedMessage id="addPassengers.wheelchairChild" />
          </Label>
          <StyledInput
            {...register('wheelchairChild', {
              validate: handleValidateWheelchairUsers
            })}
            type="number"
            inputMode="numeric"
            id="wheelchairChild"
            data-testid="wheelchair-child"
            data-cy="wheelchair-child"
            validationState={errors.wheelchairChild ? 'error' : ''}
            min={0}
            max={MAX_NUMBER_OF_WHEELCHAIR_USERS}
          />
        </AssistanceField>
        <AssistanceField>
          <Label htmlFor="wheelchairCompanions">
            <FormattedMessage id="addPassengers.wheelchairCompanions" />
          </Label>
          <StyledInput
            {...register('wheelchairCompanions', {
              validate: handleValidateWheelchairCompanions
            })}
            type="number"
            inputMode="numeric"
            id="wheelchairCompanions"
            data-testid="wheelchair-companions"
            data-cy="wheelchair-companions"
            validationState={errors.wheelchairCompanions ? 'error' : ''}
            min={0}
            max={totalWheelchairUsers}
          />
        </AssistanceField>
        <AssistanceLabel>
          <FormattedMessage id="addPassengers.allSpecialAssistancePassengers" />
        </AssistanceLabel>
        <AssistanceField>
          <Label>
            <FormattedMessage id="addPassengers.wheelchairUsers" />
          </Label>
          <StyledInput data-testid="total-wheelchair-users" data-cy="total-wheelchair-users" disabled value={totalWheelchairUsers} />
        </AssistanceField>
        <AssistanceField>
          <Label>
            <FormattedMessage id="addPassengers.wheelchairCompanions" />
          </Label>
          <StyledInput
            data-testid="total-wheelchair-companions"
            data-cy="total-wheelchair-companions"
            disabled
            value={totalWheelchairCompanions}
          />
        </AssistanceField>
        <AssistanceField>
          <Label>
            <FormattedMessage id="addPassengers.nonWheelchairCompanions" />
          </Label>
          <StyledInput
            data-testid="total-non-wheelchair-companions"
            data-cy="total-non-wheelchair-companions"
            disabled
            value={totalNonWheelchairCompanions}
          />
        </AssistanceField>
        {errors.wheelchairAdults || errors.wheelchairChild ? (
          <ErrorField data-testid="wheelchair-passengers-error" data-cy="wheelchair-passengers-error" id="wheelchair-passengers-error">
            <FormattedMessage id="addPassengers.wheelchairPassengersError" />
          </ErrorField>
        ) : null}
        {errors.wheelchairCompanions ? (
          <ErrorField
            data-testid="wheelchair-companion-passengers-error"
            data-cy="wheelchair-companion-passengers-error"
            id="wheelchair-companion-passengers-error"
          >
            <FormattedMessage id="addPassengers.wheelchairCompanionPassengersError" />
          </ErrorField>
        ) : null}
        {actionComponent}
      </AssistanceContainer>
    </StyledAssistancePax>
  );
};

WheelchairPassengers.propTypes = {
  register: PropTypes.func,
  getValues: PropTypes.func,
  errors: PropTypes.shape({
    wheelchairAdults: PropTypes.object,
    wheelchairChild: PropTypes.object,
    wheelchairCompanions: PropTypes.object
  }),
  trigger: PropTypes.func,
  numberOfWheelchairUsers: PropTypes.number,
  numberOfWheelchairCompanions: PropTypes.number,
  numberOfNonWheelchairCompanions: PropTypes.number,
  actionComponent: PropTypes.node,
  setValue: PropTypes.func
};

export default WheelchairPassengers;
